import { combineReducers, configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { dataTableReducer, dataTableReducerPath } from '@/features/dataTable/store/dataTableSlice';
import {
  partyPaymentInformationReducer,
  partyPaymentInformationReducerPath,
} from '@/features/drawer/components/PartyPaymentInformation/store/partyPaymentInformation.slice';
import { editModeReducer, editModeReducerPath } from '@/features/editMode/store/editModeSlice';
import { entityDetailsReducer, entityDetailsReducerPath } from '@/features/entityDetails/store/entityDetailsSlice';
import { geographyReducer, geographyReducerPath } from '@/features/geography/store/geographySlice';
import { globalSearchReducer, globalSearchReducerPath } from '@/features/globalSearch/store/globalSearchSlice';
import {
  googleAddressValidationApiReducer,
  googleAddressValidationApiReducerPath,
} from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import {
  googlePlacesApiReducer,
  googlePlacesApiReducerPath,
} from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import {
  notificationsSliceReducer,
  notificationsSliceReducerPath,
} from '@/features/notifications/store/notificationsSlice';
import { partiesReducer, partiesReducerPath } from '@/features/parties/store/partiesSlice';
import { serviceModalReducer, serviceModalReducerPath } from '@/features/serviceModal/store/serviceModalSlice';
import { snacksReducer, snacksReducerPath } from '@/features/snacks/store/snacksSlice';
import { systemSliceReducer, systemSliceReducerPath } from '@/features/system/store/systemSlice';
import { addressesSliceReducer, addressesSliceReducerPath } from '@/domains/addresses/store/addressesSlice';
import {
  createTransactionFromAdjustmenReducer,
  createTransactionFromAdjustmenReducerPath,
} from '@/domains/adjustments/store/createTransactionFromAdjustment.slice';
import {
  deleteAdjustmentReducer,
  deleteAdjustmentReducerPath,
} from '@/domains/adjustments/store/deleteAdjustment.slice';
import { adminUsersSliceReducer, adminUsersSliceReducerPath } from '@/domains/adminUsers/store/adminUsersSlice';
import {
  assignmentQueuesReducer,
  assignmentQueuesReducerPath,
} from '@/domains/assignmentQueues/store/assignmentQueueSlice';
import { authSliceReducer, authSliceReducerPath } from '@/domains/auth/store/authSlice';
import { beneficiarySliceReducer, beneficiarySliceReducerPath } from '@/domains/beneficiaries/store/beneficiarySlice';
import {
  createBeneficiaryReducer,
  createBeneficiaryReducerPath,
} from '@/domains/beneficiaries/store/createBeneficiarySlice';
import {
  benefitCalculationDetailsReducer,
  benefitCalculationDetailsReducerPath,
} from '@/domains/benefits/store/BenefitCalculationSlice';
import { benefitsReducer, benefitsReducerPath } from '@/domains/benefits/store/benefitsSlice';
import { allClaimSetsListReducer, allClaimSetsListReducerPath } from '@/domains/claimSets/store/allClaimSetsListSlice';
import { claimSetCreationReducer, claimSetCreationReducerPath } from '@/domains/claimSets/store/claimSetCreationSlice';
import { claimSetsReducer, claimSetsReducerPath } from '@/domains/claimSets/store/claimSetSlice';
import { claimantsListReducer, claimantsListReducerPath } from '@/domains/claimants/store/claimantsListSlice';
import { createClaimantReducer, createClaimantReducerPath } from '@/domains/claimants/store/createClaimantSlice';
import { claimsReducer, claimsReducerPath } from '@/domains/claims/claimsSlice/claimsSlice';
import { createClaimReducer, createClaimReducerPath } from '@/domains/claims/claimsSlice/createClaimSlice';
import {
  claimSetClaimListReducer,
  claimSetClaimListReducerPath,
} from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { allClaimsListReducer, allClaimsListReducerPath } from '@/domains/claims/store/allClaimsSlice';
import { contactReducer, contactReducerPath } from '@/domains/contacts/store/contactSlice';
import { createContactReducer, createContactReducerPath } from '@/domains/contacts/store/createContactSlice';
import { coveragesReducer, coveragesReducerPath } from '@/domains/coverages/store/coveragesSlice';
import { decisionTablesReducer, decisionTablesReducerPath } from '@/domains/decisionTables/store/decisionTablesSlice';
import {
  documentInstancesReducer,
  documentInstancesReducerPath,
} from '@/domains/documents/store/slices/documentInstancesSlice/documentInstancesSlice';
import {
  systemDocumentsReducer,
  systemDocumentsReducerPath,
} from '@/domains/documents/store/slices/documentsSlice/documentsSlice';
import {
  generateDocumentReducer,
  generateDocumentReducerPath,
} from '@/domains/documents/store/slices/generateDocumentSlice/generateDocumentSlice';
import { helperTagsReducer, helperTagsReducerPath } from '@/domains/helperTags/store/helperTagsSlice';
import { createInsuredReducer, createInsuredReducerPath } from '@/domains/insureds/store/createInsuredSlice';
import { insuredsReducer, insuredsReducerPath } from '@/domains/insureds/store/insuredSlice';
import { milestonesReducer, milestonesReducerPath } from '@/domains/milestones/store/milestonesSlice';
import {
  createNoteDocumentReducer,
  createNoteDocumentReducerPath,
} from '@/domains/notes/store/slices/createNoteDocumentSlice';
import { createNoteReducer, createNoteReducerPath } from '@/domains/notes/store/slices/createNoteSlice';
import { noteReducer, noteReducerPath } from '@/domains/notes/store/slices/noteSlice';
import { partyDetailsReducer, partyDetailsReducerPath } from '@/domains/partyDetails/store/partyDetails.slice';
import {
  partyDetailsTabListReducer,
  partyDetailsTabListReducerPath,
} from '@/domains/partyDetails/store/partyDetailsTabList.slice';
import { paymentsReducer, paymentsReducerPath } from '@/domains/payments/features/PaymentsSection/store/payments.slice';
import {
  addPaymentReducer,
  addPaymentReducerPath,
} from '@/domains/payments/features/paymentModals/modals/AddPayment/store/addPayment.slice';
import {
  addPaymentMethodReducer,
  addPaymentMethodReducerPath,
} from '@/domains/payments/features/paymentModals/modals/AddPaymentMethod/store/addPaymentMethod.slice';
import {
  addPaymentSplitReducer,
  addPaymentSplitReducerPath,
} from '@/domains/payments/features/paymentModals/modals/AddPaymentSplit/store/addPaymentSplit.slice';
import {
  editPaymentMethodReducer,
  editPaymentMethodReducerPath,
} from '@/domains/payments/features/paymentModals/modals/EditPaymentMethod/store/editPaymentMethod.slice';
import {
  addAdjustmentReducer,
  addAdjustmentReducerPath,
} from '@/domains/payments/features/paymentModals/modals/adjustments/store/addAdjustment.slice';
import {
  addPayeeReducer,
  addPayeeReducerPath,
} from '@/domains/payments/features/paymentModals/modals/payee/store/addPayee.slice';
import { policySliceReducer, policySliceReducerPath } from '@/domains/policies/store/policySlice';
import { producerReducer, producerReducerPath } from '@/domains/producer/store/producer.slice';
import { createProducerReducer, createProducerReducerPath } from '@/domains/producers/store/createProducerSlice';
import { linkProducerReducer, linkProducerReducerPath } from '@/domains/producers/store/linkProducerSlice';
import { producersReducer, producersReducerPath } from '@/domains/producers/store/producersSlice';
import { profileDetailsReducer, profileDetailsReducerPath } from '@/domains/profileDetails/store/profileDetailsSlice';
import { recentActivityReducer, recentActivityReducerPath } from '@/domains/recentActivity/store/recentActivitySlice';
import { taskListReducer, taskListReducerPath } from '@/domains/tasks/features/myTaskList/slice/taskListSlice';
import { createTaskReducer, createTaskReducerPath } from '@/domains/tasks/store/createTaskSlice';
import { tasksSliceReducer, tasksSliceReducerPath } from '@/domains/tasks/store/tasksSlice';
import {
  transactionDetailsReducer,
  transactionDetailsReducerPath,
} from '@/domains/transactions/store/transactionSlice';
import { userSliceReducer, userSliceReducerPath } from '@/domains/user/store/userSlice';
import { apiErrorHandler, unauthorizedErrorHandler } from '@/store/middleware';
import rootSaga from '@/store/sagas';

const sagaMiddleware = createSagaMiddleware();

export const rootReducer = combineReducers({
  [systemSliceReducerPath]: systemSliceReducer,
  [authSliceReducerPath]: authSliceReducer,
  [claimsReducerPath]: claimsReducer,
  [claimSetsReducerPath]: claimSetsReducer,
  [insuredsReducerPath]: insuredsReducer,
  [contactReducerPath]: contactReducer,
  [createContactReducerPath]: createContactReducer,
  [claimantsListReducerPath]: claimantsListReducer,
  [createClaimantReducerPath]: createClaimantReducer,
  [createBeneficiaryReducerPath]: createBeneficiaryReducer,
  [createTaskReducerPath]: createTaskReducer,
  [adminUsersSliceReducerPath]: adminUsersSliceReducer,
  [serviceModalReducerPath]: serviceModalReducer,
  [userSliceReducerPath]: userSliceReducer,
  [beneficiarySliceReducerPath]: beneficiarySliceReducer,
  [editModeReducerPath]: editModeReducer,
  [policySliceReducerPath]: policySliceReducer,
  [claimSetClaimListReducerPath]: claimSetClaimListReducer,
  [partyDetailsTabListReducerPath]: partyDetailsTabListReducer,
  [recentActivityReducerPath]: recentActivityReducer,
  [snacksReducerPath]: snacksReducer,
  [entityDetailsReducerPath]: entityDetailsReducer,
  [geographyReducerPath]: geographyReducer,
  [claimSetCreationReducerPath]: claimSetCreationReducer,
  [googlePlacesApiReducerPath]: googlePlacesApiReducer,
  [googleAddressValidationApiReducerPath]: googleAddressValidationApiReducer,
  [addressesSliceReducerPath]: addressesSliceReducer,
  [profileDetailsReducerPath]: profileDetailsReducer,
  [systemDocumentsReducerPath]: systemDocumentsReducer,
  [tasksSliceReducerPath]: tasksSliceReducer,
  [coveragesReducerPath]: coveragesReducer,
  [createClaimReducerPath]: createClaimReducer,
  [taskListReducerPath]: taskListReducer,
  [allClaimSetsListReducerPath]: allClaimSetsListReducer,
  [allClaimsListReducerPath]: allClaimsListReducer,
  [milestonesReducerPath]: milestonesReducer,
  [createInsuredReducerPath]: createInsuredReducer,
  [notificationsSliceReducerPath]: notificationsSliceReducer,
  [globalSearchReducerPath]: globalSearchReducer,
  [partiesReducerPath]: partiesReducer,
  [benefitsReducerPath]: benefitsReducer,
  [addPaymentReducerPath]: addPaymentReducer,
  [benefitCalculationDetailsReducerPath]: benefitCalculationDetailsReducer,
  [deleteAdjustmentReducerPath]: deleteAdjustmentReducer,
  [generateDocumentReducerPath]: generateDocumentReducer,
  [transactionDetailsReducerPath]: transactionDetailsReducer,
  [addAdjustmentReducerPath]: addAdjustmentReducer,
  [paymentsReducerPath]: paymentsReducer,
  [addPayeeReducerPath]: addPayeeReducer,
  [noteReducerPath]: noteReducer,
  [createNoteReducerPath]: createNoteReducer,
  [createNoteDocumentReducerPath]: createNoteDocumentReducer,
  [addPaymentMethodReducerPath]: addPaymentMethodReducer,
  [createTransactionFromAdjustmenReducerPath]: createTransactionFromAdjustmenReducer,
  [editPaymentMethodReducerPath]: editPaymentMethodReducer,
  [addPaymentSplitReducerPath]: addPaymentSplitReducer,
  [assignmentQueuesReducerPath]: assignmentQueuesReducer,
  [helperTagsReducerPath]: helperTagsReducer,
  [partyDetailsReducerPath]: partyDetailsReducer,
  [partyPaymentInformationReducerPath]: partyPaymentInformationReducer,
  [producerReducerPath]: producerReducer,
  [producersReducerPath]: producersReducer,
  [linkProducerReducerPath]: linkProducerReducer,
  [dataTableReducerPath]: dataTableReducer,
  [createProducerReducerPath]: createProducerReducer,
  [documentInstancesReducerPath]: documentInstancesReducer,
  [decisionTablesReducerPath]: decisionTablesReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: false,
      immutableCheck: false,
    }).concat(unauthorizedErrorHandler, apiErrorHandler, sagaMiddleware),
});

sagaMiddleware.run(rootSaga);
